'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function PDFToImagesTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [format, setFormat] = useState<'jpeg' | 'png'>('jpeg');
  const [scale, setScale] = useState(2);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPDF = useCallback(async (f: File) => {
    if (f.type !== 'application/pdf') { alert('Please upload a PDF file.'); return; }
    setFile(f);
    setPreviews([]);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const arrayBuffer = await f.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPageCount(pdf.numPages);
    } catch (err) {
      console.error(err);
      alert('Failed to load PDF.');
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) loadPDF(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadPDF(f);
  };

  const handleConvert = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    setPreviews([]);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const urls: string[] = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress(Math.round((i / pdf.numPages) * 90));
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, 0.92);
        urls.push(dataUrl);

        // Download each page
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `page-${i}.${format}`;
        a.click();
        await new Promise((r) => setTimeout(r, 200));
      }

      setProgress(100);
      setPreviews(urls.slice(0, 4));
    } catch (err) {
      console.error(err);
      alert('Conversion failed. Please try again.');
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-5">
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between bg-surface rounded-xl p-3">
            <div>
              <p className="font-semibold text-text-primary text-sm">{file.name}</p>
              <p className="text-text-muted text-xs">{formatBytes(file.size)} · {pageCount} pages</p>
            </div>
            <button onClick={() => { setFile(null); setPageCount(0); setPreviews([]); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-text-primary text-sm mb-2 block">Output Format</label>
              <div className="flex gap-2">
                {(['jpeg', 'png'] as const).map((f) => (
                  <button key={f} onClick={() => setFormat(f)} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${format === f ? 'bg-brand-blue text-white' : 'bg-surface text-text-secondary hover:bg-gray-100'}`}>
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-semibold text-text-primary text-sm mb-2 block">Quality Scale: {scale}x</label>
              <input type="range" min={1} max={3} step={0.5} value={scale} onChange={(e) => setScale(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: '#2563EB' }} />
              <div className="flex justify-between text-xs text-text-muted mt-1"><span>Smaller</span><span>Sharper</span></div>
            </div>
          </div>

          {processing && (
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1"><span>Converting pages…</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-brand-blue rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          {previews.length > 0 && (
            <div>
              <p className="text-sm text-text-secondary mb-2">Preview (first {previews.length} pages):</p>
              <div className="grid grid-cols-4 gap-2">
                {previews.map((url, i) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img key={i} src={url} alt={`Page ${i + 1}`} className="w-full rounded-lg border border-gray-100 object-cover aspect-[3/4]" />
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleConvert}
            disabled={processing}
            className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Converting…</>
            ) : (
              `Convert ${pageCount} Page${pageCount !== 1 ? 's' : ''} to ${format.toUpperCase()}`
            )}
          </button>
        </div>
      )}
    </div>
  );
}
